import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../axiosConfig';

const DocumentUpload = ({ onComplete, onBack }) => {
  const [primaryDoc, setPrimaryDoc] = useState(null);
  const [secondaryDoc, setSecondaryDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  // optional: drag & drop state
  const [draggingPrimary, setDraggingPrimary] = useState(false);
  const [draggingSecondary, setDraggingSecondary] = useState(false);

  const onDrop = (e, type) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    if (type === 'primary') setPrimaryDoc(f);
    else setSecondaryDoc(f);
  };

  const submitDocuments = async () => {
    if (!primaryDoc || !secondaryDoc) {
      alert('Please upload both required documents');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('verification_method', 'document');
    formData.append('primary_document', primaryDoc);
    formData.append('secondary_document', secondaryDoc);

    try {
      await axiosInstance.post('/verification/submit-docs/', formData, {
        headers: { 'Content-Type': undefined }, // let browser set multipart boundary
        transformRequest: [(data) => data],
      });

      // make sure profile reflects method in case user entered this page directly
      try {
        await axiosInstance.patch('/member-portal/my-profile/', {
          verification_method: 'document',
          verification_status: 'pending',
        });
      } catch {} // non-blocking

      if (onComplete) onComplete();
      else navigate('/member-portal');
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading documents. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="document-upload">
      <div className="upload-header">
        <button className="back-button-link" onClick={onBack}>&larr; Back</button>
        <h3>Upload Verification Documents</h3>
        <div className="security-note">
          🔒 For verification use only by PrivateIQ. Documents are securely stored and never shared.
        </div>
      </div>

      <div className="upload-sections">
        <div className="upload-section">
          <h4>Primary Proof</h4>

          <FileUploadZone
            acceptedTypes="image/*,.pdf"
            title="Recent Club Statement / Invoice .PDF"
            description="Must show your name and a recent date."
            selectedFile={primaryDoc}
            onFileSelect={(f) => setPrimaryDoc(f)}
            dragging={draggingPrimary}
            setDragging={setDraggingPrimary}
            onDrop={(e) => onDrop(e, 'primary')}
          />
        </div>

        <div className="upload-section">
          <h4>Secondary Proof</h4>
          <FileUploadZone
            acceptedTypes="image/*"
            title="Club Portal Screenshot"
            description="Show your name in the member portal."
            selectedFile={secondaryDoc}
            onFileSelect={(f) => setSecondaryDoc(f)}
            dragging={draggingSecondary}
            setDragging={setDraggingSecondary}
            onDrop={(e) => onDrop(e, 'secondary')}
          />
        </div>
      </div>

      <div className="upload-actions">
        <button className="piq-btn-ghost" onClick={onBack}>
          Back to Method Selection
        </button>
        <button
          className="piq-btn-primary"
          onClick={submitDocuments}
          disabled={!primaryDoc || !secondaryDoc || uploading}
        >
          {uploading ? 'Uploading…' : 'Submit for Review'}
        </button>
      </div>
    </div>
  );
};

const FileUploadZone = ({
  onFileSelect,
  acceptedTypes,
  title,
  description,
  selectedFile,
  dragging,
  setDragging,
  onDrop,
}) => {
  const inputRef = useRef(null);

  const onChange = (e) => {
    const f = e.target.files?.[0];
    if (f) onFileSelect(f);
  };

  return (
    <div
      className={`file-upload-zone ${dragging ? 'dragging' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { setDragging(false); onDrop(e); }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={acceptedTypes}
        onChange={onChange}
        style={{ display: 'none' }}
      />
      <div className="upload-content">
        <div className="upload-icon">⬆️</div>
        <h5>{title}</h5>
        <p>{description}</p>
        <div className="upload-cta">
          <button className="piq-btn-secondary" onClick={() => inputRef.current?.click()}>
            Choose file
          </button>
          <span className="upload-hint">or drag & drop</span>
        </div>
        {selectedFile && (
          <div className="selected-file">✅ {selectedFile.name}</div>
        )}
      </div>
    </div>
  );
};

export default DocumentUpload;
